import { useEffect, useState } from 'react';

import { useUnits } from '../../units/UnitContext';
import { formatDualDimension } from '../../units/unitConversions';
import { metricClassificationLabel } from './metricThreadDisplay';
import MetricThreadProfile from './MetricThreadProfile';
import PreDrillResults from './PreDrillResults';

const Dimension = ({ value }) => {
  const { unitSystem } = useUnits();
  const formatted = formatDualDimension(value, unitSystem);
  return <><strong>{formatted.primary}</strong><span className="metricSecondaryDimension">{formatted.secondary}</span></>;
};

const LimitRange = ({ minimum, maximum, maximumNotSpecified = false }) => (
  <dl className="metricLimits">
    <div><dt>Minimum</dt><dd><Dimension value={minimum} /></dd></div>
    <div><dt>Maximum</dt><dd>{maximumNotSpecified ? <strong>Not specified</strong> : <Dimension value={maximum} />}</dd></div>
  </dl>
);

const MetricThreadResult = ({
  result,
  preDrillResult = null,
  preDrillLoading = false,
  preDrillError = '',
  formingDiameterInput = '',
  onFormingDiameterChange,
}) => {
  const internal = result.input.side === 'internal';
  const [geometryExpanded, setGeometryExpanded] = useState(false);
  const [expandedPreDrillTechnology, setExpandedPreDrillTechnology] = useState(null);
  const additionalGeometryId = 'metric-additional-geometry';
  const cuttingPreDrillAvailable = Number.isFinite(Number(
    preDrillResult?.cutting?.recommendedPreDrillDiameterMm
  ));
  const formingPreDrillAvailable = Number.isFinite(Number(
    preDrillResult?.forming?.recommendedPreDrillDiameterMm
  ));

  useEffect(() => {
    setExpandedPreDrillTechnology(null);
  }, [result.designation]);

  const togglePreDrillTechnology = technology => {
    setExpandedPreDrillTechnology(current => current === technology ? null : technology);
  };

  return (
    <article className="metricResult" aria-labelledby="metric-result-title">
      <header>
        <p>{internal ? 'Internal Metric Thread' : 'External Metric Thread'}</p>
        <h2 id="metric-result-title">{result.designation}</h2>
        <span className="metricClassification">{metricClassificationLabel(result.classification)}</span>
      </header>

      <MetricThreadProfile
        result={result}
        preDrillDiameterMm={expandedPreDrillTechnology === 'cutting'
          ? preDrillResult?.cutting?.recommendedPreDrillDiameterMm
          : undefined}
        showGeometry={geometryExpanded}
        onGeometryToggle={() => setGeometryExpanded(expanded => !expanded)}
        geometryControlsId={additionalGeometryId}
        expandedPreDrillTechnology={expandedPreDrillTechnology}
        onPreDrillTechnologyToggle={togglePreDrillTechnology}
        cuttingPreDrillAvailable={cuttingPreDrillAvailable}
        formingPreDrillAvailable={formingPreDrillAvailable}
      />

      {internal && (
        <PreDrillResults
          result={preDrillResult}
          loading={preDrillLoading}
          error={preDrillError}
          formingDiameterInput={formingDiameterInput}
          onFormingDiameterChange={onFormingDiameterChange}
          expandedTechnology={expandedPreDrillTechnology}
        />
      )}

      <div className="metricResultGrid">
        <section>
          <h3>Major diameter {internal ? 'D' : 'd'}</h3>
          <LimitRange
            minimum={result.majorDiameter.minimumMm}
            maximum={result.majorDiameter.maximumMm}
            maximumNotSpecified={internal && result.majorDiameter.maximumStatus === 'not_specified'}
          />
        </section>
        <section>
          <h3>Pitch diameter {internal ? 'D2' : 'd2'}</h3>
          <LimitRange minimum={result.pitchDiameter.minimumMm} maximum={result.pitchDiameter.maximumMm} />
        </section>
        {internal ? (
          <section>
            <h3>Minor diameter D1</h3>
            <LimitRange minimum={result.minorDiameter.minimumMm} maximum={result.minorDiameter.maximumMm} />
          </section>
        ) : (
          <>
            <section>
              <h3>d1 reference</h3>
              <dl className="metricLimits"><div><dt>Reference maximum</dt><dd><Dimension value={result.minorDiameterReference.maximumMm} /></dd></div></dl>
              <p className="metricResultHelp">Reference diameter only; this is not a normative d3 root limit.</p>
            </section>
            <section>
              <h3>Root diameter d3</h3>
              <LimitRange
                minimum={result.rootDiameter.minimumMm}
                maximum={result.rootDiameter.maximumMm ?? result.rootDiameter.designH6Mm}
              />
            </section>
          </>
        )}
      </div>

      <details
        id={additionalGeometryId}
        className="metricAdditionalGeometry"
        open={geometryExpanded}
        onToggle={event => setGeometryExpanded(event.currentTarget.open)}
      >
        <summary>Additional geometry</summary>
        <div className="metricAdditionalGeometryContent">
          <section aria-labelledby="metric-basic-diameters-title">
            <h3 id="metric-basic-diameters-title">Basic diameters</h3>
            <dl className="metricLimits metricGeometryGrid">
              <div><dt>Major diameter</dt><dd><Dimension value={result.basicGeometry.majorDiameterMm} /></dd></div>
              <div><dt>Pitch diameter</dt><dd><Dimension value={result.basicGeometry.pitchDiameterMm} /></dd></div>
              <div><dt>Minor diameter reference</dt><dd><Dimension value={result.basicGeometry.minorDiameterReferenceMm} /></dd></div>
            </dl>
          </section>

          <section aria-labelledby="metric-basic-profile-title">
            <h3 id="metric-basic-profile-title">ISO Metric basic profile</h3>
            <dl className="metricLimits metricGeometryGrid">
              <div><dt>Pitch P</dt><dd><Dimension value={result.basicGeometry.pitchMm} /></dd></div>
              <div><dt>Fundamental triangle height H</dt><dd><Dimension value={result.basicGeometry.fundamentalTriangleHeightMm} /></dd></div>
              <div><dt>Basic thread height H1</dt><dd><Dimension value={result.basicGeometry.basicThreadHeightH1Mm} /></dd></div>
              <div><dt>Flank profile height 3H/8</dt><dd><Dimension value={result.basicGeometry.h3Over8Mm} /></dd></div>
              <div><dt>Basic truncation H/4</dt><dd><Dimension value={result.basicGeometry.hOver4Mm} /></dd></div>
              <div><dt>Basic truncation H/8</dt><dd><Dimension value={result.basicGeometry.hOver8Mm} /></dd></div>
              <div><dt>Half pitch P/2</dt><dd><Dimension value={result.basicGeometry.halfPitchMm} /></dd></div>
              <div><dt>Basic crest width P/8</dt><dd><Dimension value={result.basicGeometry.crestFlatWidthP8Mm} /></dd></div>
              <div><dt>Basic root width P/4</dt><dd><Dimension value={result.basicGeometry.rootFlatWidthP4Mm} /></dd></div>
              <div><dt>Included angle</dt><dd><strong>{result.basicGeometry.includedAngleDeg}°</strong></dd></div>
              <div><dt>Flank angle</dt><dd><strong>{result.basicGeometry.flankAngleDeg}°</strong></dd></div>
            </dl>
          </section>

          {!internal && result.externalDesignProfile && (
            <section aria-labelledby="metric-external-profile-title">
              <h3 id="metric-external-profile-title">External design profile</h3>
              <dl className="metricLimits metricGeometryGrid">
                <div><dt>Thread height h3</dt><dd><Dimension value={result.externalDesignProfile.threadHeightH3Mm} /></dd></div>
                <div>
                  <dt>d1 → d3 diametral offset</dt>
                  <dd><strong>H/6 = </strong><Dimension value={result.externalDesignProfile.d1ToD3DiametralOffsetMm ?? result.externalDesignProfile.fullRootRadiusRMm} /></dd>
                </div>
                <div>
                  <dt>Full root radius R</dt>
                  <dd>
                    <Dimension value={result.externalDesignProfile.fullRootRadiusRMm} />
                    <span className="metricGeometryNote">Full-radius external root design value.</span>
                  </dd>
                </div>
                <div>
                  <dt>Minimum partial root radius R1,min</dt>
                  <dd>
                    <Dimension value={result.externalDesignProfile.minimumPartialRootRadiusR1Mm} />
                    <span className="metricGeometryNote">Minimum radius for a partial-radius external root; the actual radius may be specified by the applicable product documentation.</span>
                  </dd>
                </div>
              </dl>
            </section>
          )}
        </div>
      </details>

      {result.toleranceRule.specialToleranceRequiresSpecification && (
        <aside className="metricSpecialNote" role="note">
          <strong>Calculated tolerance rule</strong>
          <p>This combination uses calculated tolerance rules rather than only listed table values. The resulting tolerance should be explicitly specified in the product documentation.</p>
        </aside>
      )}
    </article>
  );
};

export default MetricThreadResult;
